import omit from 'lodash/omit';
import get from 'lodash/get';
import map from 'lodash/map';
import keys from 'lodash/keys';
import flow from 'lodash/flow';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import not from 'ramda/src/not';
import reduce from 'ramda/src/reduce';
import flatten from 'ramda/src/flatten';
import uniq from 'ramda/src/uniq';
import repeat from 'ramda/src/repeat';
import zipObj from 'ramda/src/zipObj';
import propOr from 'ramda/src/propOr';
import assoc from 'ramda/src/assoc';
import curry from 'ramda/src/curry';
import pipe from 'ramda/src/pipe';
import sortBy from 'lodash/sortBy';
import {
  fullName,
} from '../../../common/helpers/users';
import { t } from '../../../../modules/infra/i18n';

const OMIT_KEYS = [
  'unassigned',
];

export const getServicesPercentage = item => (item.servicesPercentage ? `${item.servicesPercentage}%` : '-');
export const getProductsPercentage = item => (item.productsPercentage ? `${item.productsPercentage}%` : '-');
const servicesDueTo = (unassigned, item) => (unassigned ? 0 : item.servicesRevenue + item.servicesInstantPayoutFee);
const productsDueTo = (unassigned, item) => (unassigned ? 0 : item.productsRevenue + item.productsInstantPayoutFee);

const filterKeys = item => omit(item, OMIT_KEYS);
const getFilteredKeys = flow([filterKeys, keys]);

export const mapSingleServiceReport = item => ({
  Name: fullName(item.barber),
  Card: item.totalServicesPosRevenue + get(item, 'totalServicesStripeRevenue', 0) || 0,
  Cash: item.totalServicesCashRevenue,
  'GC Used': item.totalServicesGiftCardRevenue,
  'GC Bought': item.giftCardForCommission,
  Total: item.totalServicesRevenue,
  'Barber Commission': getServicesPercentage(item),
  'Due to Barbers': servicesDueTo(item.unassigned, item) || 0,
  'Taxes Collected': item.totalServicesTaxAmount,
  'Due to Shop': item.servicesDueToShop + item.totalServicesTaxAmount,
  unassigned: item.unassigned,
});

export const mapSingleProductReport = item => ({
  Name: fullName(item.barber),
  Card: item.totalProductsPosRevenue,
  Cash: item.totalProductsCashRevenue,
  'GC Used': item.totalProductsGiftCardRevenue,
  Total: item.totalProductsRevenue,
  'Barber Commission': getProductsPercentage(item),
  'Due to Barbers': productsDueTo(item.unassigned, item) || 0,
  'Taxes Collected': item.totalProductsTaxAmount,
  'Due to Shop': item.productsDueToShop + item.totalProductsTaxAmount,
  unassigned: item.unassigned,
});

const renameKeys = curry((keysMap, obj) =>
  reduce((acc, key) => assoc(keysMap[key] || key, obj[key], acc), {}, keys(obj))
);

const renameDueToTransferred = renameKeys({
  'Due to Barbers': 'Transferred to Barber',
  'Due to Shop': 'Transferred to Shop',
});

const getTranslatedColumnLabel = (columnName) => {
  switch (columnName) {
    case 'Name':
      return t('reports:payouts.columns.name');
    case 'Card':
      return t('reports:payouts.columns.card');
    case 'Cash':
      return t('reports:payouts.columns.cash');
    case 'GC Used':
      return t('reports:payouts.columns.gc_used');
    case 'GC Bought':
      return t('reports:payouts.columns.gc_bought');
    case 'Total':
      return t('reports:payouts.columns.total');
    case 'Barber Commission':
      return t('reports:payouts.columns.barber_commission');
    case 'Due to Barbers':
      return t('reports:payouts.columns.due_to_barbers');
    case 'Taxes Collected':
      return t('reports:payouts.columns.taxes_collected');
    case 'Due to Shop':
      return t('reports:payouts.columns.due_to_shop');
    case 'Transferred to Barber':
      return t('reports:payouts.columns.transferred_to_barber');
    case 'Transferred to Shop':
      return t('reports:payouts.columns.transferred_to_shop');
    default:
      return columnName;
  }
};

export const mapServiceData = (data, history = false) => {
  let mapper = mapSingleServiceReport;
  if (history) {
    mapper = pipe(mapSingleServiceReport, renameDueToTransferred);
  }
  return map(data, mapper);
};

export const mapProductData = (data, history = false) => {
  let mapper = mapSingleProductReport;
  if (history) {
    mapper = pipe(mapSingleProductReport, renameDueToTransferred);
  }
  return map(data, mapper);
};

export const mapTableHeaderColumns = (tableData) => {
  const getFirstItem = items => get(items, '[0]', {});
  const columnKeys = flow([getFirstItem, getFilteredKeys])(tableData);
  
  return columnKeys.map(columnKey => ({
    key: columnKey,
    label: getTranslatedColumnLabel(columnKey),
  }));
};

export const mapTableColumns = item => getFilteredKeys(item);

export const extractUnassignedSales = (sales) => {
  const isUnassigned = propEq('unassigned', true);
  const isNotUnassigned = flow([isUnassigned, not]);

  return {
    sales: filter(isNotUnassigned, sales),
    unassigned: find(isUnassigned, sales),
  };
};

export const sumReports = (reports) => {
  const allKeys = uniq(flatten(map(reports, keys)));
  const sumObj = zipObj(allKeys, repeat(0, allKeys.length));
  return reduce((acc, item) => {
    let next = acc;
    allKeys.forEach((key) => {
      const prop = object => propOr(0, key)(object) || 0;
      next = assoc(key, prop(item) + prop(next), next);
    });
    return next;
  }, sumObj)(reports);
};

export const getBarberPercentageByRevenue = (commissionTiers, total) => {
  if (!commissionTiers.length) {
    return 0;
  }

  const sortedTiers = sortBy(commissionTiers, 'amount');
  let percentage = 0;

  for (let i = 0; i < sortedTiers.length; i += 1) {
    const tier = sortedTiers[i];

    if (total >= tier.amount) {
      percentage = tier.percentage;
    }
  }

  return percentage;
};
